var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Alert, Box, DialogContent, DialogFooter, DialogHeader, useToast } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { object, string } from 'yup';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { SupportedFileMimeTypes } from 'shared/domains/filesystem';
import { CancelButton, FormDialog, SubmitButton } from 'shared/domains/forms';
import { OptionCheckbox } from './OptionCheckbox';
import { ValidationAlert } from './ValidationAlert';
import { useDownloadFilesystemDocument } from './useDownloadFilesystemDocument';
import { useDownloadPleditorDocument } from './useDownloadPleditorDocument';
import { usePleditorDownloadAvailable } from './usePleditorDownloadAvailable';
const schema = object().shape({
    downloadType: string().oneOf(['pdf', 'docx:original', 'docx:pleditor']).required(),
});
export function DownloadDocumentDialog(props) {
    const { onClose, id } = props;
    const { addToast } = useToast();
    const { document, isPleditorDownloadAvailable, loading: loadingData, error: dataError, } = usePleditorDownloadAvailable(id);
    const { files, loading: loadingFiles, error: filesError } = useDownloadFilesystemDocument(id);
    const { downloadPleditorDocument, error: downloadError } = useDownloadPleditorDocument(document.type);
    const initialFilesLoading = !files.length && loadingFiles;
    const initialFilesError = !files.length && filesError;
    const initialLoading = loadingData || initialFilesLoading;
    const initialError = Boolean(initialFilesError || dataError);
    const downloadingFilesError = Boolean(files.length) && filesError;
    const downloadingError = downloadingFilesError || downloadError;
    const pdfFile = files.find((file) => file.type === SupportedFileMimeTypes.PDF);
    const docxFile = files.find((file) => file.type === SupportedFileMimeTypes.DOCX);
    const isPleditorDocument = document.type === DocumentType.Composed || document.type === DocumentType.Freetext;
    const showPleditorOption = isPleditorDocument && isPleditorDownloadAvailable;
    const handleSuccessfulDownload = () => {
        addToast({
            appearance: 'success',
            title: t({
                message: `${document.name} downloaded successfully`,
                comment: 'Download dialog success toast message',
            }),
        });
        onClose();
    };
    const handleSubmit = (formValues) => __awaiter(this, void 0, void 0, function* () {
        const { downloadType } = formValues;
        if (downloadType === 'pdf') {
            return pdfFile === null || pdfFile === void 0 ? void 0 : pdfFile.fetch().then(handleSuccessfulDownload);
        }
        if (downloadType === 'docx:original') {
            return docxFile === null || docxFile === void 0 ? void 0 : docxFile.fetch().then(handleSuccessfulDownload);
        }
        return downloadPleditorDocument(id, document.name).then(handleSuccessfulDownload);
    });
    return (<FormDialog size="sm" validationSchema={schema} initialValues={{ downloadType: undefined }} state={{ loading: initialLoading, error: initialError }} onClose={onClose} onSubmit={handleSubmit}>
      <DialogHeader title={t({ message: 'Download', comment: 'Download dialog header title' })} subtitle={t({
            message: 'Choose the format of your file',
            comment: 'Download dialog header subtitle',
        })} icon={<Falcon icon="download"/>}/>
      <DialogContent>
        <Box flexDirection="column" gap="md">
          {pdfFile && <OptionCheckbox type="pdf" title={pdfFile.name}/>}

          {showPleditorOption && (<OptionCheckbox type="docx:pleditor" title="DOCX" lockedReason={document.lockedReason}/>)}

          {docxFile && isPleditorDocument && !isPleditorDownloadAvailable && (<OptionCheckbox pleditorDownloadUnavailable type="docx:original" title={docxFile.name}/>)}

          {docxFile && !isPleditorDocument && <OptionCheckbox type="docx:original" title={docxFile.name}/>}

          <ValidationAlert />

          {downloadingError && (<Alert appearance="danger" message={t({
                message: 'Could not download the document, please try again',
                comment: 'Error alert message while downloading document',
            })}/>)}
        </Box>
      </DialogContent>
      <DialogFooter>
        <CancelButton size="small" onClick={onClose}>
          <Trans comment="Cancel button label">Cancel</Trans>
        </CancelButton>
        <SubmitButton size="small">
          <Trans comment="Download button label">Download</Trans>
        </SubmitButton>
      </DialogFooter>
    </FormDialog>);
}
